import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import { useForm } from 'react-hook-form';
import { FormContainer, TextFieldElement } from 'react-hook-form-mui';
import {
    Card,
    CardActions,
    CardContent,
    CardHeader,
    Stack,
} from '@mui/material';

import { BrandInput, EditButtons } from '@barboxx/admin-components';
import { PRODUCT_LINE_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { ProductLine } from '@barboxx/models';
import { Can, useBool } from '@barboxx/react-utils';

interface Props {
    productLine: ProductLine.DetailsResponse;
}

export const ProductLineDetailsCard = ({ productLine }: Props) => {
    const { products, ...rest } = productLine;
    const [editable, editableActions] = useBool(false);
    const notifications = useNotifications();

    const formContext = useForm<ProductLine.UpdateRequest>({
        resolver: yupResolver(PRODUCT_LINE_SCHEMA),
        defaultValues: rest,
    });

    const mutateUser = useMutation({
        mutationKey: ['productLine', productLine.id],
        mutationFn: (data: ProductLine.UpdateRequest) =>
            Api.Products.ProductLines.putProductLine(productLine.id, data),
    });

    const handleCancel = () => {
        editableActions.off();
        formContext.reset(undefined);
    };

    const onSubmit = async (values: ProductLine) => {
        return mutateUser
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show('Updated productLine!', {
                    severity: 'success',
                });

                editableActions.off();
                formContext.reset(data);
            })
            .catch(e => {
                notifications.show('Error Updating productLine!', {
                    severity: 'error',
                });
            });
    };

    return (
        <FormContainer onSuccess={onSubmit} formContext={formContext}>
            <Card>
                <CardHeader title="ProductLine Details" />
                <CardContent>
                    <Stack gap={2}>
                        <BrandInput disabled={!editable} />
                        <TextFieldElement
                            fullWidth
                            name="name"
                            disabled={!editable}
                            label="Name"
                        />
                        <TextFieldElement
                            fullWidth
                            name="description"
                            disabled={!editable}
                            label="Description"
                        />
                    </Stack>
                </CardContent>
                <CardActions
                    sx={{
                        justifyContent: 'flex-end',
                        paddingRight: 3,
                        paddingBottom: 3,
                    }}
                >
                    <Can I="update" a="ProductLine">
                        <EditButtons
                            formContext={formContext}
                            handleCancel={handleCancel}
                            editable={editable}
                            editableActions={editableActions}
                        />
                    </Can>
                </CardActions>
            </Card>
        </FormContainer>
    );
};
